import classNames from "classnames";
import {
	CSSProperties,
	ReactElement,
	useContext,
	useMemo,
	KeyboardEvent,
	Ref
} from "react";
import { Anchor } from "../Anchor/Anchor";
import { Icon, FontIconName } from "../Icon/Icon";
import { DropOverContext } from "../DropOver/DropOver";
import { setTrackingId } from "client/Misc/utils";
import styles from "./ActionListItem.module.less";
import { useIndention } from "./ActionListItem";

export type ActionListAnchorProps = {
	icon?: FontIconName;
	text: string;
	url: string;
	shouldOpenInNewWindow?: boolean;
	shouldNotAutoCloseContainingDropOver?: boolean;
	onKeyDown?: (args: KeyboardEvent<HTMLElement>) => void;
	trackingId?: string;
	indentionLevel?: number;
	tabIndex?: number;
	ref?: Ref<HTMLLIElement>;
};

export const ActionListAnchor = (props: ActionListAnchorProps): ReactElement => {
	const {
		icon,
		text,
		url,
		shouldOpenInNewWindow = false,
		shouldNotAutoCloseContainingDropOver = false,
		trackingId,
		indentionLevel,
		onKeyDown,
		tabIndex,
		ref
	} = props;

	const {
		onClose
	} = useContext(DropOverContext);

	const clickHandler = useMemo(
		(): (() => void) | undefined => {
			if (!onClose || shouldNotAutoCloseContainingDropOver) {
				return undefined;
			}

			return () => onClose();
		},
		[onClose, shouldNotAutoCloseContainingDropOver]
	);

	const keyDownHandler = useMemo(
		(): ((args: KeyboardEvent<HTMLElement>) => void) | undefined => {
			if (onKeyDown) {
				return (args: KeyboardEvent<HTMLElement>) => onKeyDown(args);
			}

			return undefined;
		},
		[onKeyDown]
	);

	const style: CSSProperties = useIndention(indentionLevel);

	return (
		<li
			{...setTrackingId(trackingId)}
			ref={ref}
			tabIndex={tabIndex}
			onKeyDown={keyDownHandler}
		>
			<Anchor
				className={classNames(
					styles.root,
					styles.root_hoverable
				)}
				style={style}
				href={url}
				target={shouldOpenInNewWindow ? "_blank" : undefined}
				onClick={clickHandler}
			>
				<div className={styles.contentHost}>
					{icon && (
						<span className={styles.icon}>
							<Icon value={icon}/>
						</span>
					)}

					<span className={styles.text}>
						{text}
					</span>
				</div>
			</Anchor>
		</li>
	);
};
ActionListAnchor.displayName = "ActionListAnchor";
